import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { useRestaurants } from '../hooks/use-restaurants'
import { usePrices } from '../hooks/use-prices'
import { describeStockError } from '../lib/stock-errors'
import { describeVariantUsage } from '../lib/variant-usage'
import type { VariantUsageSource } from '../lib/variant-usage'
import type { CreateVariantRequest } from '@/api/endpoints/product-stock'
import { productStockApi } from '@/api/endpoints/product-stock'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { formatCurrency } from '@/shared/utils/format-currency'
import {
  parseEuroToCents,
  parseStockQuantity,
} from '@/shared/lib/stock-quantity'

type ShareChoice = '1' | '0.5' | '0.25' | 'custom'

interface CreateVariantDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sourceSku: string
  sourceName: string
  source: VariantUsageSource
}

/** The share the dialog would send, or null while a custom share is not a valid quantity. */
export function resolveShare(
  choice: ShareChoice,
  customInput: string,
): number | null {
  if (choice !== 'custom') return Number(choice)
  const parsed = parseStockQuantity(customInput)
  return parsed.ok ? parsed.value : null
}

function shareParseError(input: string) {
  const parsed = parseStockQuantity(input)
  return parsed.ok ? null : parsed.error
}

/**
 * "Variante anlegen" (CHG-2026-09-stock-usage-and-price-twins, 10.5): names, the share of the
 * source (1, ½, ¼ or custom) with the usage one sale books, and a price per location next to the
 * source's price. Needs `product:create`; the price inputs appear only with `price:edit`, the
 * source prices only with `price:view`.
 */
export function CreateVariantDialog({
  open,
  onOpenChange,
  sourceSku,
  sourceName,
  source,
}: CreateVariantDialogProps) {
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canEditPrice = can(ACTIONS.PRICE_EDIT)
  const canViewPrice = can(ACTIONS.PRICE_VIEW)

  const [name, setName] = useState('')
  const [publicName, setPublicName] = useState('')
  const [shareChoice, setShareChoice] = useState<ShareChoice>('1')
  const [customShare, setCustomShare] = useState('')
  const [priceInputs, setPriceInputs] = useState<Record<number, string>>({})
  const [submitted, setSubmitted] = useState(false)

  const { data: restaurantsData } = useRestaurants()
  const restaurants = restaurantsData?.items ?? []
  const { data: sourcePrices } = usePrices(
    open && canViewPrice ? sourceSku : '',
  )
  const sourcePriceByRestaurant = new Map(
    (sourcePrices ?? []).map((p) => [p.restaurantId, p.amountCents]),
  )

  const share = resolveShare(shareChoice, customShare)
  const usage = share !== null ? describeVariantUsage(share, source) : null
  const shareAmbiguous =
    shareChoice === 'custom' && shareParseError(customShare) === 'ambiguousDot'
  const nameMissing = name.trim() === ''
  const priceErrors = new Set(
    Object.entries(priceInputs)
      .filter(([, value]) => value.trim() !== '')
      .filter(([, value]) => !parseEuroToCents(value).ok)
      .map(([id]) => Number(id)),
  )

  const reset = () => {
    setName('')
    setPublicName('')
    setShareChoice('1')
    setCustomShare('')
    setPriceInputs({})
    setSubmitted(false)
  }

  const createMutation = useMutation({
    mutationFn: (body: CreateVariantRequest) =>
      productStockApi.createVariant(sourceSku, body),
    onSuccess: (res) => {
      toast.success(
        tProducts('variant.createSuccess', { sku: res.variant.sku }),
      )
      queryClient.invalidateQueries({ queryKey: ['products'] })
      reset()
      onOpenChange(false)
      navigate({
        to: `/products/$sku` as '/products/$sku',
        params: { sku: res.variant.sku },
      })
    },
    onError: (err) =>
      toast.error(describeStockError(err, tProducts('variant.createFailed'))),
  })

  const handleSubmit = () => {
    setSubmitted(true)
    if (nameMissing || share === null || priceErrors.size > 0) return
    const prices = canEditPrice
      ? Object.entries(priceInputs).flatMap(([id, value]) => {
          const parsed = parseEuroToCents(value)
          return value.trim() !== '' && parsed.ok
            ? [{ restaurantId: Number(id), amountCents: parsed.cents }]
            : []
        })
      : []
    const body: CreateVariantRequest = { name: name.trim(), share }
    if (publicName.trim()) body.publicName = publicName.trim()
    if (prices.length > 0) body.prices = prices
    createMutation.mutate(body)
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) reset()
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{tProducts('variant.createTitle')}</DialogTitle>
          <DialogDescription>
            {tProducts('variant.createDescription', {
              name: sourceName,
              sku: sourceSku,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="variant-name">
              {tProducts('fields.productName')}
            </Label>
            <Input
              id="variant-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={submitted && nameMissing ? true : undefined}
              maxLength={200}
            />
            {submitted && nameMissing && (
              <p className="text-xs text-destructive">
                {tProducts('validation.nameRequired')}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="variant-public-name">
              {tProducts('variant.publicName')}
            </Label>
            <Input
              id="variant-public-name"
              value={publicName}
              onChange={(e) => setPublicName(e.target.value)}
              maxLength={200}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{tProducts('variant.share')}</Label>
          <div className="flex flex-wrap items-center gap-2">
            <ToggleGroup
              type="single"
              variant="outline"
              value={shareChoice}
              onValueChange={(value) =>
                value && setShareChoice(value as ShareChoice)
              }
              aria-label={tProducts('variant.share')}
            >
              <ToggleGroupItem value="1">
                {tProducts('variant.shareWhole')}
              </ToggleGroupItem>
              <ToggleGroupItem value="0.5">½</ToggleGroupItem>
              <ToggleGroupItem value="0.25">¼</ToggleGroupItem>
              <ToggleGroupItem value="custom">
                {tProducts('variant.shareCustom')}
              </ToggleGroupItem>
            </ToggleGroup>
            {shareChoice === 'custom' && (
              <Input
                value={customShare}
                onChange={(e) => setCustomShare(e.target.value)}
                inputMode="decimal"
                className="w-32 text-right"
                placeholder={tProducts('variant.shareCustomPlaceholder')}
                aria-label={tProducts('variant.shareCustom')}
                aria-invalid={
                  customShare.trim() !== '' && share === null ? true : undefined
                }
              />
            )}
          </div>
          <p
            className="text-sm text-muted-foreground"
            data-testid="variant-usage"
          >
            {usage
              ? tProducts(usage.key, usage.params)
              : shareAmbiguous
                ? tProducts('stockUsage.partError.ambiguousDot')
                : tProducts('variant.shareInvalid')}
          </p>
        </div>

        {canEditPrice && restaurants.length > 0 && (
          <div className="space-y-2">
            <Label>{tProducts('variant.pricesTitle')}</Label>
            <div className="rounded-lg border">
              <div className="grid grid-cols-[minmax(0,1fr)_110px_140px] gap-3 bg-muted px-3 py-2 text-xs font-semibold text-muted-foreground">
                <span>{tProducts('variant.pricesRestaurant')}</span>
                <span>{tProducts('variant.pricesSource')}</span>
                <span>{tProducts('variant.pricesVariant')}</span>
              </div>
              {restaurants.map((restaurant) => {
                const sourceCents = sourcePriceByRestaurant.get(restaurant.id)
                return (
                  <div
                    key={restaurant.id}
                    className="grid grid-cols-[minmax(0,1fr)_110px_140px] items-center gap-3 border-t px-3 py-2"
                  >
                    <span className="text-sm">{restaurant.name}</span>
                    <span className="text-sm text-muted-foreground">
                      {!canViewPrice
                        ? '–'
                        : sourceCents !== undefined
                          ? formatCurrency(sourceCents)
                          : tProducts('variant.pricesNotSold')}
                    </span>
                    <Input
                      value={priceInputs[restaurant.id] ?? ''}
                      onChange={(e) =>
                        setPriceInputs((prev) => ({
                          ...prev,
                          [restaurant.id]: e.target.value,
                        }))
                      }
                      inputMode="decimal"
                      className="text-right"
                      placeholder={tProducts('variant.pricesPlaceholder')}
                      aria-label={tProducts('variant.pricesFor', {
                        name: restaurant.name,
                      })}
                      aria-invalid={
                        priceErrors.has(restaurant.id) ? true : undefined
                      }
                    />
                  </div>
                )
              })}
            </div>
            {priceErrors.size > 0 && (
              <p className="text-xs text-destructive">
                {tProducts('prices.invalidPrice')}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {tProducts('variant.pricesHint')}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={createMutation.isPending}
          >
            {tCommon('cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={createMutation.isPending || share === null}
          >
            {createMutation.isPending
              ? tCommon('creating')
              : tProducts('variant.createConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
